import { openDB, IDBPDatabase } from 'idb';
import { StorageDriver, FileMetadata } from './StorageDriver';

const hasIndexedDb = typeof indexedDB !== 'undefined';

export class BrowserIndexedDBDriver implements StorageDriver {
  private dbPromise: Promise<IDBPDatabase>;

  constructor() {
    if (!hasIndexedDb) {
      throw new Error('IndexedDB is unavailable in this environment');
    }

    this.dbPromise = this.initializeDB();
  }

  private async initializeDB() {
    if (!hasIndexedDb) {
      throw new Error('IndexedDB is unavailable in this environment');
    }

    return openDB('MapleOS-vfs', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'path' });
        }

        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'appId' });
        }

        if (!db.objectStoreNames.contains('desktop')) {
          db.createObjectStore('desktop', { keyPath: 'key' });
        }
      },
    });
  }

  private async getDB(): Promise<IDBPDatabase> {
    try {
      return await this.dbPromise;
    } catch {
      this.dbPromise = this.initializeDB();
      return await this.dbPromise;
    }
  }

  private async runWithRetry<T>(fn: (db: IDBPDatabase) => Promise<T>): Promise<T> {
    try {
      const db = await this.getDB();
      return await fn(db);
    } catch (err: any) {
      const msg = String(err?.message || '');
      if (err?.name === 'InvalidStateError' || /connection is closing/i.test(msg) || /closing/i.test(msg)) {
        this.dbPromise = this.initializeDB();
        const db = await this.dbPromise;
        return await fn(db);
      }
      throw err;
    }
  }

  async readFile(path: string): Promise<string | null> {
    return await this.runWithRetry(async (db) => {
      const fileData = await db.get('files', path);
      return fileData?.content || null;
    });
  }

  async writeFile(path: string, content: string): Promise<void> {
    await this.runWithRetry(async (db) => {
      const fileData: FileMetadata = {
        path,
        type: 'file',
        size: new TextEncoder().encode(content).length,
        modified: new Date(),
        content,
      };
      await db.put('files', fileData);
    });
  }

  async writeFileWithMeta(path: string, content: string, modified?: Date): Promise<void> {
    await this.runWithRetry(async (db) => {
      const fileData: FileMetadata = {
        path,
        type: 'file',
        size: new TextEncoder().encode(content).length,
        modified: modified ?? new Date(),
        content,
      };
      await db.put('files', fileData);
    });
  }

  async deleteFile(path: string): Promise<void> {
    await this.runWithRetry(async (db) => {
      await db.delete('files', path);
    });
  }

  async listFiles(parentPath: string): Promise<string[]> {
    return await this.runWithRetry(async (db) => {
      const allFiles = await db.getAll('files');
      return allFiles
        .filter((file) => {
          const relativePath = file.path.substring(parentPath.length);
          const trimmedPath = relativePath.startsWith('\\') ? relativePath.substring(1) : relativePath;
          return file.path.startsWith(parentPath) && file.path !== parentPath && trimmedPath && !trimmedPath.includes('\\');
        })
        .map((file) => file.path);
    });
  }

  async exists(path: string): Promise<boolean> {
    return await this.runWithRetry(async (db) => {
      const fileData = await db.get('files', path);
      return fileData !== undefined;
    });
  }

  async createDirectory(path: string): Promise<void> {
    await this.runWithRetry(async (db) => {
      const dirData: FileMetadata = {
        path,
        type: 'folder',
        size: 0,
        modified: new Date(),
      };
      await db.put('files', dirData);
    });
  }

  async createDirectoryWithMeta(path: string, modified?: Date): Promise<void> {
    await this.runWithRetry(async (db) => {
      const dirData: FileMetadata = {
        path,
        type: 'folder',
        size: 0,
        modified: modified ?? new Date(),
      };
      await db.put('files', dirData);
    });
  }

  async saveSettings(appId: string, settings: any): Promise<void> {
    await this.runWithRetry(async (db) => {
      await db.put('settings', { appId, settings, modified: new Date() });
    });
  }

  async loadSettings(appId: string): Promise<any> {
    return await this.runWithRetry(async (db) => {
      const data = await db.get('settings', appId);
      return data?.settings || null;
    });
  }

  async saveDesktopConfig(key: string, config: any): Promise<void> {
    await this.runWithRetry(async (db) => {
      await db.put('desktop', { key, config, modified: new Date() });
    });
  }

  async loadDesktopConfig(key: string): Promise<any> {
    return await this.runWithRetry(async (db) => {
      const data = await db.get('desktop', key);
      return data?.config || null;
    });
  }

  async getAllFiles(): Promise<FileMetadata[]> {
    return await this.runWithRetry(async (db) => {
      return await db.getAll('files');
    });
  }

  async clearAll(): Promise<void> {
    await this.runWithRetry(async (db) => {
      await db.clear('files');
      await db.clear('settings');
      await db.clear('desktop');
    });
  }
}
